import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n, useTranslation } from '../../i18n/provider';
import usFlag from '../../assets/flags/us.svg';
import roFlag from '../../assets/flags/ro.svg';
import ruFlag from '../../assets/flags/ru.svg';
import logoutIcon from '../../assets/icons/logout.svg';
import styles from './Profile.module.css';

export const ProfileBar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { locale, setLocale, availableLocales, error } = useI18n();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [serverUptime, setServerUptime] = useState<string>('');
  const [localeWarning, setLocaleWarning] = useState<string | null>(null);
  const languageControlRef = useRef<HTMLDivElement | null>(null);

  const LOCALE_FLAGS: Record<string, { icon: string; label: string }> = {
    en: { icon: usFlag, label: 'EN' },
    ro: { icon: roFlag, label: 'RO' },
    ru: { icon: ruFlag, label: 'RU' },
  };

  const flagForLocale = (code: string): { icon: string; label: string } => {
    return LOCALE_FLAGS[code] || LOCALE_FLAGS.en;
  };

  // Fetch server uptime when profile is expanded
  useEffect(() => {
    if (isExpanded && isAuthenticated) {
      const fetchServerUptime = async () => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL?.replace('/api', '');
          const response = await fetch(`${apiUrl}/health`);
          const data = await response.json();
          if (data.uptime) {
            setServerUptime(new Date(data.uptime).toLocaleString());
          }
        } catch (error) {
          console.error('Failed to fetch server uptime:', error);
        }
      };

      fetchServerUptime();
    }
  }, [isAuthenticated, isExpanded]);

  useEffect(() => {
    if (!isLanguageMenuOpen) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (languageControlRef.current && !languageControlRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isLanguageMenuOpen]);

  useEffect(() => {
    if (!error) {
      return;
    }

    setLocaleWarning(error);

    const timeoutId = window.setTimeout(() => {
      setLocaleWarning(null);
    }, 6000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [error]);

  const handleToggle = () => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const handleClose = () => {
    setIsExpanded(false);
  };

  return (
    <>
      <div className={styles.profileSection} onClick={handleToggle}>
        <div className={styles.profileContent}>
          <div className={styles.avatar}>{user?.username?.charAt(0).toUpperCase() || 'G'}</div>
          <div>
            <div className={styles.userName}>{user?.username || t('profile.guest')}</div>
            <div className={styles.userSubtext}>{isAuthenticated ? t('profile.viewProfile') : t('profile.loginRegister')}</div>
          </div>
        </div>
        <div
          ref={languageControlRef}
          className={styles.languageControl}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <button
            type="button"
            className={styles.languageButton}
            aria-label={t('language.label')}
            aria-expanded={isLanguageMenuOpen}
            data-testid="language-selector-button"
            onClick={(e) => {
              e.stopPropagation();
              setIsLanguageMenuOpen((prev) => !prev);
            }}
          >
            <img
              src={flagForLocale(locale).icon}
              alt={t('language.label')}
              className={styles.languageFlagIcon}
            />
            <span className={styles.languageCode}>{flagForLocale(locale).label}</span>
            <span className={styles.languageChevron}>▾</span>
          </button>

          {isLanguageMenuOpen && (
            <div className={styles.languageMenu}>
              {availableLocales.map((entry) => {
                const isActive = entry.code === locale;
                return (
                  <button
                    key={entry.code}
                    type="button"
                    className={`${styles.languageOption} ${isActive ? styles.languageOptionActive : ''}`}
                    data-testid={`language-option-${entry.code}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsLanguageMenuOpen(false);
                      if (entry.code !== locale) {
                        void setLocale(entry.code);
                      }
                    }}
                  >
                    <img
                      src={flagForLocale(entry.code).icon}
                      alt={entry.label}
                      className={styles.languageFlagIcon}
                    />
                    <span className={styles.languageCode}>{flagForLocale(entry.code).label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {isAuthenticated ? (
          <button
            className={styles.logoutButton}
            data-testid="profile-logout-button"
            onClick={(e) => {
              e.stopPropagation();
              logout();
            }}
            title="Logout"
          >
            <img
              src={logoutIcon}
              alt="Logout"
              style={{
                width: '40px',
                height: '40px'
              }}
            />
          </button>
        ) : (
          <button
            className={styles.logoutButton}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/login');
            }}
            title="Login / Register"
            style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}
          >
            Login
          </button>
        )}
      </div>
      {localeWarning && (
        <div className={styles.localeWarning} role="status" aria-live="polite">
          {localeWarning}
        </div>
      )}

      {isExpanded && isAuthenticated && (
        <div className={styles.profileOverlay} onClick={handleClose}>
          <div className={styles.profilePanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.profilePanelHeader}>
              <h2 className={styles.profilePanelTitle}>{t('profile.userInfo')}</h2>
              <button className={styles.closeButton} onClick={handleClose}>
                ✕
              </button>
            </div>
            <div className={styles.profilePanelContent}>
              <div className={styles.profileInfoItem}>
                <span className={styles.profileInfoLabel}>{t('profile.username')}:</span>
                <span className={styles.profileInfoValue}>{user?.username || 'N/A'}</span>
              </div>
              <div className={styles.profileInfoItem}>
                <span className={styles.profileInfoLabel}>{t('profile.email')}:</span>
                <span className={styles.profileInfoValue}>{user?.email || 'N/A'}</span>
              </div>
              <div className={styles.profileInfoItem}>
                <span className={styles.profileInfoLabel}>{t('profile.userId')}:</span>
                <span className={styles.profileInfoValue}>{user?.id || 'N/A'}</span>
              </div>
              <div className={styles.profileInfoItem}>
                <span className={styles.profileInfoLabel}>{t('profile.serverUptime')}:</span>
                <span className={styles.profileInfoValue}>{serverUptime || t('profile.loading')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
